import { AlignCenter, AlignLeft, AlignRight, Bold, ImagePlus, Italic, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { describeElement, type StyleDefaults, type StylePatch } from "@/lib/label-canvas/elements"
import { getOrnament } from "@/lib/label-canvas/ornaments"
import {
  LIMITS,
  type BottleType,
  type CanvasData,
  type LabelElement,
  type OrnamentElement,
} from "@/lib/label-canvas/schema"
import { BottleTiles, DecimalStepper, FontList, Swatches, fontSupports } from "./controls"

// Inspector panels. The desktop inspector stacks them in the right column;
// the phone reuses the same panels inside bottom sheets.

export type InspectorHandlers = {
  onBottle: (bottle: BottleType) => void
  onStyle: (patch: StylePatch) => void
  onText: (text: string) => void
  onBackground: (hex: string) => void
  onDelete: () => void
  onPickImage: () => void
  /** Resize the selected ornament to this width, mm (its aspect is kept). */
  onOrnamentWidth: (width: number) => void
}

function PanelTitle({ label, kind }: { label: string; kind?: string }) {
  return (
    <div className="studio-panel-title">
      <div className="field-label">{label}</div>
      {kind && <span className="studio-panel-kind">{kind}</span>}
    </div>
  )
}

export function BottlePanel({ doc, onBottle }: { doc: CanvasData; onBottle: InspectorHandlers["onBottle"] }) {
  return (
    <section className="studio-panel" aria-label="Bottle">
      <PanelTitle label="Bottle" />
      <BottleTiles value={doc.bottle} onChange={onBottle} />
    </section>
  )
}

export function BackgroundPanel({
  doc,
  onBackground,
  title = "Label background",
}: {
  doc: CanvasData
  onBackground: InspectorHandlers["onBackground"]
  title?: string
}) {
  return (
    <section className="studio-panel" aria-label={title}>
      <PanelTitle label={title} />
      <Swatches
        label="Label background colour"
        value={doc.background.color}
        onChange={(hex) => hex && onBackground(hex)}
      />
    </section>
  )
}

const mm = (v: number) => `${v.toFixed(1)} mm`

function TextControls({
  style,
  onStyle,
}: {
  style: StyleDefaults
  onStyle: InspectorHandlers["onStyle"]
}) {
  const supports = fontSupports(style.font)
  return (
    <>
      <div className="studio-field">
        <div className="field-label">Font</div>
        <FontList value={style.font} onChange={(font) => onStyle({ font })} />
      </div>

      <div className="studio-field">
        <div className="field-label">Size · letter height</div>
        <DecimalStepper
          label="Text size"
          value={style.size_mm}
          step={0.5}
          min={LIMITS.textSizeMm.min}
          max={LIMITS.textSizeMm.max}
          format={mm}
          onChange={(size_mm) => onStyle({ size_mm })}
        />
      </div>

      <div className="studio-field">
        <div className="field-label">Style &amp; alignment</div>
        <div className="studio-row">
          <div className="studio-seg" role="group" aria-label="Weight and slope">
            <button
              type="button"
              aria-label="Bold"
              title={supports.bold ? "Bold" : "This font has no bold"}
              aria-pressed={style.weight === "bold" && supports.bold}
              disabled={!supports.bold}
              onClick={() => onStyle({ weight: style.weight === "bold" ? "regular" : "bold" })}
            >
              <Bold aria-hidden="true" />
            </button>
            <button
              type="button"
              aria-label="Italic"
              title={supports.italic ? "Italic" : "This font has no italic"}
              aria-pressed={style.italic && supports.italic}
              disabled={!supports.italic}
              onClick={() => onStyle({ italic: !style.italic })}
            >
              <Italic aria-hidden="true" />
            </button>
          </div>
          <div className="studio-seg" role="group" aria-label="Alignment">
            {(
              [
                ["left", "Align left", AlignLeft],
                ["center", "Centre", AlignCenter],
                ["right", "Align right", AlignRight],
              ] as const
            ).map(([align, name, Icon]) => (
              <button
                key={align}
                type="button"
                aria-label={name}
                title={name}
                aria-pressed={style.align === align}
                onClick={() => onStyle({ align })}
              >
                <Icon aria-hidden="true" />
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="studio-field">
        <div className="field-label">Letter-spacing</div>
        <DecimalStepper
          label="Letter-spacing"
          value={style.letter_spacing_em}
          step={0.02}
          min={LIMITS.letterSpacingEm.min}
          max={LIMITS.letterSpacingEm.max}
          format={(v) => `${v >= 0 ? "" : "−"}${Math.abs(v).toFixed(2)} em`}
          onChange={(letter_spacing_em) => onStyle({ letter_spacing_em })}
        />
      </div>

      <div className="studio-field">
        <div className="field-label">Colour</div>
        <Swatches label="Text colour" value={style.color} onChange={(hex) => hex && onStyle({ color: hex })} />
      </div>
    </>
  )
}

function FrameControls({
  element,
  style,
  onStyle,
}: {
  element: LabelElement
  style: StyleDefaults
  onStyle: InspectorHandlers["onStyle"]
}) {
  const isLine = element.type === "line"
  return (
    <>
      <div className="studio-field">
        <div className="field-label">{isLine ? "Thickness" : "Stroke width"}</div>
        <DecimalStepper
          label={isLine ? "Divider thickness" : "Stroke width"}
          value={style.stroke_mm}
          step={0.1}
          min={LIMITS.strokeMm.min}
          max={LIMITS.strokeMm.max}
          format={mm}
          onChange={(stroke_mm) => onStyle({ stroke_mm })}
        />
      </div>
      <div className="studio-field">
        <div className="field-label">Colour</div>
        <Swatches label="Frame colour" value={style.stroke} onChange={(hex) => hex && onStyle({ stroke: hex })} />
      </div>
      {element.type === "rect" && (
        <div className="studio-field">
          <div className="field-label">Fill</div>
          <Swatches label="Frame fill" allowNone value={style.fill} onChange={(fill) => onStyle({ fill })} />
        </div>
      )}
    </>
  )
}

/**
 * The width range of an ornament, mm: at least 3 mm on its long side, and
 * no larger than the label allows at its aspect.
 */
export function ornamentWidthRange(el: OrnamentElement, label: CanvasData["label"]) {
  const aspect = getOrnament(el.asset)?.aspect ?? (el.height > 0 ? el.width / el.height : 1)
  const min = aspect >= 1 ? LIMITS.minSizeMm : LIMITS.minSizeMm * aspect
  const max = Math.min(label.width_mm, label.height_mm * aspect)
  return { aspect, min, max }
}

function OrnamentControls({
  element,
  label,
  onStyle,
  onWidth,
}: {
  element: OrnamentElement
  label: CanvasData["label"]
  onStyle: InspectorHandlers["onStyle"]
  onWidth: InspectorHandlers["onOrnamentWidth"]
}) {
  const { min, max } = ornamentWidthRange(element, label)
  return (
    <>
      <div className="studio-field">
        <div className="field-label">Colour</div>
        <Swatches label="Ornament colour" value={element.color} onChange={(hex) => hex && onStyle({ color: hex })} />
      </div>
      <div className="studio-field">
        <div className="field-label">Width · proportions kept</div>
        <DecimalStepper
          label="Ornament width"
          value={element.width}
          step={1}
          min={min}
          max={max}
          format={mm}
          onChange={onWidth}
        />
      </div>
    </>
  )
}

/** Properties of the selected element — or, with nothing selected, the style of the next text. */
export function PropertiesPanel({
  selected,
  style,
  handlers,
  textFieldId,
  label,
  hasUpload,
}: {
  selected: LabelElement | null
  style: StyleDefaults
  handlers: InspectorHandlers
  textFieldId: string
  /** The label's size, for the ornament width range. */
  label: CanvasData["label"]
  /** An image has been uploaded (an image element without one is an empty slot). */
  hasUpload: boolean
}) {
  if (!selected) {
    return (
      <section className="studio-panel" aria-label="Next text style">
        <PanelTitle label="Selection" kind="Nothing selected" />
        <div className="studio-empty-note">
          Choose an element on the label to adjust it. The settings below apply to the next line of text you
          add.
        </div>
        <div className="mt-5">
          <TextControls style={style} onStyle={handlers.onStyle} />
        </div>
      </section>
    )
  }

  return (
    <section className="studio-panel" aria-label="Selected element">
      <PanelTitle label="Selection" kind={describeElement(selected).replace(/^Text “.*”$/, "Text")} />

      {selected.type === "text" && (
        <>
          <div className="studio-field">
            <label htmlFor={textFieldId} className="field-label">
              Wording
            </label>
            <Textarea
              id={textFieldId}
              rows={3}
              maxLength={LIMITS.maxTextLength}
              value={selected.text}
              placeholder="Your wording"
              onChange={(e) => handlers.onText(e.target.value)}
              className="form-control-cellar studio-textarea"
            />
          </div>
          <div className="studio-field">
            <TextControls style={style} onStyle={handlers.onStyle} />
          </div>
        </>
      )}

      {(selected.type === "rect" || selected.type === "ellipse" || selected.type === "line") && (
        <FrameControls element={selected} style={style} onStyle={handlers.onStyle} />
      )}

      {selected.type === "ornament" && (
        <OrnamentControls
          element={selected}
          label={label}
          onStyle={handlers.onStyle}
          onWidth={handlers.onOrnamentWidth}
        />
      )}

      {selected.type === "image" &&
        (hasUpload ? (
          <div className="studio-field">
            <Button type="button" variant="ghost-hairline" size="sm" onClick={handlers.onPickImage}>
              <ImagePlus className="mr-2 h-4 w-4" aria-hidden="true" />
              Replace image
            </Button>
            <div className="field-hint">Drag the corners on the label to resize; the proportions are kept.</div>
          </div>
        ) : (
          <div className="studio-field">
            <Button type="button" variant="gold" size="sm" onClick={handlers.onPickImage}>
              <ImagePlus className="mr-2 h-4 w-4" aria-hidden="true" />
              {selected.placeholder?.trim() || "Upload an image"}
            </Button>
            <div className="field-hint">
              PNG, JPEG or WebP, up to 10 MB. Your image fits inside this space; drag its corners to resize it.
            </div>
          </div>
        ))}

      <div className="studio-field">
        <button type="button" className="studio-danger" onClick={handlers.onDelete}>
          <Trash2 aria-hidden="true" />
          Delete element
        </button>
      </div>
    </section>
  )
}
